import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useModal } from '../../../components/layout';
import KnowledgeBaseForm from '../../../components/knowledge-bases/KnowledgeBaseForm.tsx';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import {
    User,
    KnowledgeBaseFormData,
    KBUsageInfo
} from "../../../types/knowledge-base.ts";
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

type SourceType = 'files' | 'websites' | 'apis' | '';

interface KnowledgeBaseResponse {
    id: string;
    name: string;
    description: string;
    source_type: SourceType;
    chunk_size: number;
    overlap: number;
    embedding_model: string;
    data_source_ids: string[];
    status: string;
}

const Edit: React.FC = () => {
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [kbStatus, setKbStatus] = useState<string>('');
    const [kbUsage, setKbUsage] = useState<KBUsageInfo | null>(null);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [localUser] = useState<User | null>(user);
    const modalState = useModal();

    const [formData, setFormData] = useState<KnowledgeBaseFormData>({
        name: '',
        description: '',
        sourceType: '',
        chunkSize: 500,
        overlap: 50,
        embeddingModel: 'text-embedding-ada-002',
        dataSourceIds: []
    });

    const fetchIndex = async () => {
        setIsInitialLoading(true);
        try {
            const data = await apiService.get<KnowledgeBaseResponse>(`${API_ENDPOINTS.INDEXES.GET}/${id}`);
            console.log('Fetched KB data:', data);

            setFormData({
                name: data.name,
                description: data.description || '',
                sourceType: data.source_type,
                chunkSize: data.chunk_size,
                overlap: data.overlap,
                embeddingModel: data.embedding_model,
                dataSourceIds: data.data_source_ids || []
            });

            setKbStatus(data.status);
            await fetchKBUsage();
        } catch (err) {
            console.error('Error fetching knowledge-base:', err);
            modalState.setError(err instanceof Error ? err.message : 'Failed to fetch knowledge-base');
        } finally {
            setIsInitialLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchIndex();
        }
    }, [id]);

    const fetchKBUsage = async () => {
        if (!id) return;

        try {
            const data = await apiService.get<KBUsageInfo>(`${API_ENDPOINTS.INDEXES.USAGE}/${id}`);
            setKbUsage(data);
        } catch (err) {
            console.error('Error fetching KB usage:', err);
            setKbUsage({
                isInUse: false,
                applications: []
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const indexPayload = {
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                chunk_size: formData.chunkSize,
                overlap: formData.overlap,
                embedding_model: formData.embeddingModel,
                data_source_ids: formData.dataSourceIds
            };

            await apiService.put(`${API_ENDPOINTS.INDEXES.UPDATE}/${id}`, indexPayload);

            modalState.setSuccess('Knowledge Base updated successfully');
            navigate('/knowledge-bases');
        } catch (err) {
            console.error('Error updating knowledge base:', err);
            modalState.setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        modalState.setIsLoading(true);

        try {
            await apiService.delete(`${API_ENDPOINTS.INDEXES.DELETE}/${id}`);
            modalState.setSuccess('Knowledge base deleted successfully');
            navigate('/knowledge-bases');
        } catch (err) {
            console.error('Error deleting knowledge-base:', err);
            modalState.setError(err instanceof Error ? err.message : 'Failed to delete knowledge base');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleFieldChange = <K extends keyof KnowledgeBaseFormData>(
        field: K,
        value: KnowledgeBaseFormData[K]
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (!localUser) return null;

    return (
        <div className="page-container">
            <div className="content-header">
                <h5 className="mb-4 fw-semibold">Edit Knowledge Base</h5>
                    {(isInitialLoading || modalState.isLoading) ? (
                        <ContentSkeleton/>
                    ) : (
                        <KnowledgeBaseForm
                            formData={formData}
                            onSubmit={handleSubmit}
                            onChange={handleFieldChange}
                            onDelete={handleDelete}
                            error={modalState.error}
                            isLoading={modalState.isLoading}
                            mode="edit"
                            kbUsage={kbUsage}
                            status={kbStatus}
                        />
                    )}
                </div>
            </div>
            );
            };

            export default Edit;